import React from 'react';

import { motion } from 'framer-motion';
import { Award, Briefcase, Building2, Moon, Sun, X, Crown, Heart, Sparkles, Dog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import SocialIconsContainer from 'components/SocialIconsContainer';
import ThemeToggle from 'components/ThemeToggle';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUserId, useUserName, useUserCompany, useUserCompanyRole, useUserAvatarRaca, useUserArquetipo, useUserPersonalidade, useUserCaracteristicas } from 'hooks/useCurrentUser';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { useUserAvatar } from 'hooks/useUserAvatar';
import { useUserSocialNetworks } from 'hooks/useUserSocialNetworks';
import { glassmorphismClasses, animationVariants, combineTokens } from 'lib/design-tokens';
import { cn } from 'lib/utils';
import { useTheme } from 'providers/ThemeProvider';

/**
 * User statistics data structure
 * @interface UserStats
 * @property {number} profileCompletion - Profile completion percentage (0-100)
 * @property {number} [activeProjects] - Number of active projects (optional)
 * @property {number} [achievements] - Number of achievements earned (optional)
 */
interface UserStats {
  profileCompletion: number;
  activeProjects?: number;
  achievements?: number;
}

/**
 * Props for the CockpitUserCardRedesigned component
 * @interface UserCardProps
 * @property {() => void} [onEditProfile] - Callback when Edit Profile button is clicked
 * @property {() => void} [onSettings] - Callback when Settings button is clicked
 * @property {UserStats} [stats] - User statistics (defaults to zeros if not provided)
 * @property {string} [className] - Additional CSS classes
 */
export interface UserCardProps {
  onEditProfile?: () => void;
  onSettings?: () => void;
  onClose?: () => void;
  stats?: UserStats;
  className?: string;
}

/**
 * CockpitUserCard Component
 *
 * User profile card for the Cockpit dashboard with glassmorphism design.
 * Displays user information, company role, profile completion, and quick actions.
 *
 * @component
 * @param {UserCardProps} props - Component props
 * @returns {JSX.Element} User profile card with avatar, stats, and action buttons
 *
 * @features
 * - User avatar with initials fallback
 * - First name and last name displayed on separate lines
 * - Company role and company name (format: Role | Company)
 * - Profile completion progress bar with color coding:
 *   - Green (text-success): 80%+
 *   - Yellow (text-warning): 50-79%
 *   - Red (text-destructive): <50%
 * - Stats grid (active projects, achievements)
 * - Professional info section (placeholder for future data)
 * - Professional network section (placeholder for future data)
 * - Action buttons:
 *   - "Basic Data": Navigates to IndividualBio page
 *   - "Company Data": Navigates to BusinessBio page
 * - Framer Motion fade-in animation
 * - Full theme support via CSS variables
 * - Glassmorphism styling via design tokens
 *
 * @hooks
 * - useUser: Access user context data (first_name, last_name, company_role, company.name)
 * - useUserImage: Fetch user profile image
 * - useMenuRoutes: Get menu routes for navigation
 * - useMenuSelection: Handle menu selection and navigation
 *
 * @example
 * ```tsx
 * <CockpitUserCard
 *   stats={{
 *     profileCompletion: 85,
 *     activeProjects: 5,
 *     achievements: 12,
 *   }}
 *   onEditProfile={() => console.log('Navigate to basic data')}
 *   onSettings={() => console.log('Navigate to company data')}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Minimal usage with default stats
 * <CockpitUserCard />
 * ```
 */
const CockpitUserCard: React.FC<UserCardProps> = ({
  onEditProfile,
  onSettings,
  onClose,
  stats,
  className,
}) => {
  const { t } = useTranslation('profile');
  const userId = useUserId();
  const userName = useUserName();
  const { firstName, lastName } = userName || { firstName: '', lastName: '' };
  const { avatarUrl } = useUserAvatar(userId || '');
  const company = useUserCompany();
  const companyRole = useUserCompanyRole();
  const avatarRaca = useUserAvatarRaca();
  const arquetipo = useUserArquetipo();
  const personalidade = useUserPersonalidade();
  const caracteristicas = useUserCaracteristicas();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const menuRoutes = useMenuRoutes();
  const { selectMenuByDocumentId } = useMenuSelection();
  const { socialNetworks, loading: socialLoading } = useUserSocialNetworks();

  // Filter only filled social networks
  const filledSocialNetworks = socialNetworks.filter(
    network => network.social_network_username && network.social_network_username.trim() !== ''
  );

  // Default stats if not provided
  const userStats: UserStats = stats || {
    profileCompletion: 0,
    activeProjects: 0,
    achievements: 0,
  };

  /**
   * Navigate to BasicPersonalData (Dados Básicos) page
   */
  const handleNavigateToDadosBasicos = () => {
    // Close the card first to avoid stale state
    onEditProfile?.();

    const route = menuRoutes.find(r => r.documentId === 'ah06bmqiaqi7oxigam2836u1');
    if (route) {
      const menuContext = {
        documentId: route.documentId,
        path: route.path,
        preserveLevel: true,
      };
      selectMenuByDocumentId(route.documentId, true, undefined, route.path, true);
      navigate(route.path, { state: { menuContext } });
    }
  };

  /**
   * Navigate to Business Bio (Dados da Empresa) page
   */
  const handleNavigateToDadosEmpresa = () => {
    // Close the card first to avoid stale state
    onSettings?.();

    const route = menuRoutes.find(r => r.documentId === 'ki9ao5w75ds5ykbeytbvm2dd');
    if (route) {
      const menuContext = {
        documentId: route.documentId,
        path: route.path,
        preserveLevel: true,
      };
      selectMenuByDocumentId(route.documentId, true, undefined, route.path, true);
      navigate(route.path, { state: { menuContext } });
    }
  };

  /**
   * Generates user initials from full name for avatar fallback
   * @param {string} [name] - User's full name
   * @returns {string} Uppercase initials (2 characters max)
   *
   * @example
   * getInitials("John Doe") // Returns "JD"
   * getInitials("Alice") // Returns "AL"
   * getInitials(undefined) // Returns "U"
   */
  const getInitials = (name?: string) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Determines color class based on profile completion percentage
   * @param {number} percentage - Profile completion percentage (0-100)
   * @returns {string} Tailwind CSS color class
   *
   * @example
   * getCompletionColor(85) // Returns "text-success"
   * getCompletionColor(60) // Returns "text-warning"
   * getCompletionColor(30) // Returns "text-destructive"
   */
  const getCompletionColor = (percentage: number) => {
    if (percentage >= 80) return 'text-success';
    if (percentage >= 50) return 'text-warning';
    return 'text-destructive';
  };

  return (
    <motion.div
      initial={animationVariants.fadeIn.initial}
      animate={animationVariants.fadeIn.animate}
      transition={animationVariants.fadeIn.transition}
      className={cn('h-full', className)}
    >
      <Card
        className={combineTokens(
          glassmorphismClasses.card,
          glassmorphismClasses.cardHover,
          'w-full h-full overflow-hidden shadow-xl'
        )}
      >
        {/* Header with Avatar */}
        <CardHeader className="pb-4 relative">
          {/* Close Button */}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label={t('labels.close')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <div className="flex flex-col items-center gap-4">
            {/* Avatar */}
            <Avatar className="h-48 w-48 border-4 border-primary/30">
              <AvatarImage
                src={avatarUrl || undefined}
                alt={firstName && lastName ? `${firstName} ${lastName}` : 'User'}
              />
              <AvatarFallback className="bg-gradient-to-br from-primary/30 to-primary/10 text-4xl font-bold text-primary">
                {getInitials(firstName && lastName ? `${firstName} ${lastName}` : undefined)}
              </AvatarFallback>
            </Avatar>

            {/* Social Networks - only show if there are filled networks */}
            {filledSocialNetworks.length > 0 && (
              <SocialIconsContainer
                socialNetworks={filledSocialNetworks}
                socialLoading={socialLoading}
                circle
                iconSize={16}
                gap="10px"
                className="mt-1"
              />
            )}

            {/* User Name - First and Last Name on separate lines */}
            <div className="text-center space-y-1.5 w-full px-4">
              <div className="space-y-0.5">
                <h3
                  className="text-h3 font-bold text-foreground break-words"
                  title={firstName || t('labels.firstName')}
                >
                  {firstName || t('labels.firstName')}
                </h3>
                <p
                  className="text-h3 font-bold text-foreground break-words"
                  title={lastName || t('labels.lastName')}
                >
                  {lastName || t('labels.lastName')}
                </p>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Profile Completion */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-small font-medium text-foreground">{t('labels.profileComplete')}</span>
              <Badge
                variant="outline"
                className={combineTokens(
                  'text-small font-semibold',
                  getCompletionColor(userStats.profileCompletion)
                )}
              >
                {userStats.profileCompletion}%
              </Badge>
            </div>
            <div className="w-full bg-secondary/30 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-primary/60 transition-all duration-500"
                style={{ width: `${userStats.profileCompletion}%` }}
              />
            </div>
          </div>

          {/* Stats Grid */}
          {(userStats.activeProjects !== undefined || userStats.achievements !== undefined) && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              {userStats.activeProjects !== undefined && (
                <div
                  className={combineTokens(
                    glassmorphismClasses.card,
                    'p-3 rounded-lg text-center border border-primary/10'
                  )}
                >
                  <Briefcase className="h-5 w-5 text-primary mx-auto mb-1" />
                  <p className="text-body font-bold text-foreground">{userStats.activeProjects}</p>
                  <p className="text-xs text-muted-foreground">{t('labels.projects')}</p>
                </div>
              )}
              {userStats.achievements !== undefined && (
                <div
                  className={combineTokens(
                    glassmorphismClasses.card,
                    'p-3 rounded-lg text-center border border-primary/10'
                  )}
                >
                  <Award className="h-5 w-5 text-primary mx-auto mb-1" />
                  <p className="text-body font-bold text-foreground">{userStats.achievements}</p>
                  <p className="text-xs text-muted-foreground">{t('labels.achievements')}</p>
                </div>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-primary/10 pt-4" />

          {/* Avatar Animal Section - Highlight */}
          <div className="space-y-2">
            <div className="rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 bg-primary/20 rounded-full p-3">
                  <Dog className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold text-foreground">
                    {avatarRaca || t('labels.breedNotProvided')}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('labels.behavioralAvatar')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Behavioral Profile Section */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t('labels.behavioralProfile')}
            </h4>
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2.5">
              <div className="flex items-start gap-2">
                <Crown className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-foreground">{t('labels.archetype')}</p>
                  <p className="text-sm text-muted-foreground">{arquetipo || t('labels.notProvided')}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Heart className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-foreground">{t('labels.personality')}</p>
                  <p className="text-sm text-muted-foreground">{personalidade || t('labels.notProvided')}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-foreground">{t('labels.characteristics')}</p>
                  <p className="text-sm text-muted-foreground">{caracteristicas || t('labels.notProvided')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-primary/10" />

          {/* Professional Info Section */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t('labels.professionalInfo')}
            </h4>
            {companyRole || company?.name ? (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
                {companyRole && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">{t('labels.position')}</p>
                      <p className="text-sm font-medium text-foreground truncate">{companyRole}</p>
                    </div>
                  </div>
                )}
                {company?.name && (
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">{t('labels.company')}</p>
                      <p className="text-sm font-medium text-foreground truncate">{company.name}</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="min-h-[60px] rounded-lg border border-dashed border-primary/20 flex items-center justify-center">
                <p className="text-xs text-muted-foreground/70 italic">
                  {t('labels.noInfoAvailable')}
                </p>
              </div>
            )}
          </div>

          {/* Professional Network Section */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t('labels.professionalNetwork')}
            </h4>
            <div className="min-h-[60px] rounded-lg border border-dashed border-primary/20 flex items-center justify-center">
              <p className="text-xs text-muted-foreground/70 italic">{t('labels.noConnectionAvailable')}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleNavigateToDadosBasicos}
              className="w-full text-xs font-medium border-primary/30 hover:bg-primary/10 flex items-center justify-center gap-1.5 px-2"
            >
              <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{t('labels.basicData')}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNavigateToDadosEmpresa}
              className="w-full text-xs font-medium border-primary/30 hover:bg-primary/10 flex items-center justify-center gap-1.5 px-2"
            >
              <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{t('labels.companyData')}</span>
            </Button>
          </div>

          {/* Theme Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/30 transition-colors cursor-pointer group">
            <div className="flex items-center gap-2">
              {isDark ? (
                <Moon className="h-4 w-4 text-primary group-hover:scale-110 transition-transform" />
              ) : (
                <Sun className="h-4 w-4 text-primary group-hover:scale-110 transition-transform" />
              )}
              <span className="text-sm font-medium text-foreground">{t('labels.theme')}</span>
            </div>
            <ThemeToggle />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export { CockpitUserCard };
export default CockpitUserCard;
